import React, { useMemo, useState, useEffect } from 'react';
import { Download, Search } from 'lucide-react';
import * as XLSX from 'xlsx';

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';

  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const year = date.getUTCFullYear();

  return `${day}/${month}/${year}`;
};

const formatPrice = (value) => {
  if (value === undefined || value === null) return '$0.00';
  const num = parseFloat(value);
  if (Number.isNaN(num)) return '$0.00';
  return '$' + num.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const convertPrice = (value, record, targetMoneda, tiposCambio = []) => {
  const num = parseFloat(value) || 0;
  // El backend retorna 'Moneda' en mayúsculas, o 'moneda' en minúsculas
  const recordMoneda = record.Moneda || record.Moneda_Consumo || record.moneda || record.moneda_consumo;
  
  if (!recordMoneda || recordMoneda === targetMoneda) return num;
  
  // Solo convertir si el registro está en MXN y queremos USD
  if (recordMoneda === 'MXN' && targetMoneda === 'USD') {
    // Buscar el tipo de cambio apropiado
    const tc = getTipoCambioForRecord(record, tiposCambio);
    const resultado = num / (tc || 18.0); // Default 18.0 si no se encuentra
    console.log(`   📊 MXN→USD: ${num} / ${tc || 18.0} = ${resultado}`);
    return resultado;
  }
  
  // Si está en USD y queremos MXN (no es típico, pero lo soportamos)
  if (recordMoneda === 'USD' && targetMoneda === 'MXN') {
    // Buscar tipo de cambio USD -> MXN (inverso)
    const tc = getTipoCambioForRecord(record, tiposCambio);
    const resultado = num * (tc || 18.0);
    console.log(`   📊 USD→MXN: ${num} * ${tc || 18.0} = ${resultado}`);
    return resultado;
  }
  
  return num;
};

// Helper para obtener el tipo de cambio correcto según proveedor, fecha y moneda
const getTipoCambioForRecord = (record, tiposCambio = []) => {
  if (!record || tiposCambio.length === 0) return null;
  
  // El backend retorna campos con mayúsculas
  const Proveedor = record.Proveedor || record.proveedor;
  const Fecha_Consumo = record.Mes || record.Fecha_Consumo || record.fecha_consumo; // Puede ser 'Mes' o 'Fecha_Consumo'
  const Moneda_Consumo = record.Moneda || record.Moneda_Consumo || record.moneda || record.moneda_consumo;
  
  if (Moneda_Consumo !== 'MXN') return null; // Solo convertimos MXN
  
  // Extraer la fecha del Fecha_Consumo (formato YYYY-MM-DD o YYYY-MM)
  const recordDate = Fecha_Consumo?.split('T')[0] || Fecha_Consumo;
  // Si es formato YYYY-MM, convertir a YYYY-MM-01
  const normalizedDate = recordDate && recordDate.length === 7 ? `${recordDate}-01` : recordDate;
  
  // Mapeo de proveedores para buscar el tipo de cambio correcto
  const getProviderKey = (proveedor) => {
    if (!proveedor) return null;
    
    const upper = proveedor.toUpperCase();
    
    // GOOGLE-MASTER -> buscar GOOGLE
    if (upper.includes('GOOGLE')) return 'GOOGLE';
    
    // AMAZON-* -> buscar AMAZON
    if (upper.includes('AMAZON')) return 'AMAZON';
    
    // MICROSOFT-* -> usar AMAZON como fallback
    if (upper.includes('MICROSOFT')) return 'AMAZON';
    
    // HUAWEI-* -> usar AMAZON como fallback
    if (upper.includes('HUAWEI')) return 'AMAZON';
    
    return null;
  };
  
  const providerKey = getProviderKey(Proveedor);
  
  console.log(`🔎 Buscando TC para: fecha=${normalizedDate}, proveedor=${Proveedor} (key=${providerKey}), moneda=${Moneda_Consumo}`);
  
  // Buscar tipo de cambio: proveedor exacto primero, luego fallback
  if (providerKey === 'GOOGLE') {
    // Buscar GOOGLE-MASTER exactamente o cualquier GOOGLE para esa fecha
    const tc = tiposCambio.find(t => {
      const tFecha = t.Fecha_Consumo || t.fecha_consumo;
      const tMoneda = t.Moneda || t.moneda;
      const tProveedor = t.Proveedor || t.proveedor;
      const match = tFecha === normalizedDate && 
             tMoneda === 'MXN' &&
             tProveedor?.toUpperCase().includes('GOOGLE');
      return match;
    });
    if (tc?.Tipo_Cambio || tc?.tipo_cambio) {
      const rate = tc.Tipo_Cambio || tc.tipo_cambio;
      console.log(`   ✅ TC GOOGLE encontrado: ${rate}`);
      return rate;
    }
  }
  
  if (providerKey === 'AMAZON' || providerKey === 'MICROSOFT' || providerKey === 'HUAWEI') {
    // Buscar AMAZON para esa fecha
    const tc = tiposCambio.find(t => {
      const tFecha = t.Fecha_Consumo || t.fecha_consumo;
      const tMoneda = t.Moneda || t.moneda;
      const tProveedor = t.Proveedor || t.proveedor;
      return tFecha === normalizedDate && 
             tMoneda === 'MXN' &&
             tProveedor?.toUpperCase().includes('AMAZON');
    });
    if (tc?.Tipo_Cambio || tc?.tipo_cambio) {
      const rate = tc.Tipo_Cambio || tc.tipo_cambio;
      console.log(`   ✅ TC AMAZON encontrado: ${rate}`);
      return rate;
    }
  }
  
  // Si no encuentra exacto, buscar cualquiera para esa fecha
  console.log(`   ⚠️  No encontró TC específico, buscando fallback para fecha=${normalizedDate}`);
  const anyTC = tiposCambio.find(t => {
    const tFecha = t.Fecha_Consumo || t.fecha_consumo;
    const tMoneda = t.Moneda || t.moneda;
    return tFecha === normalizedDate && tMoneda === 'MXN';
  });
  if (anyTC?.Tipo_Cambio || anyTC?.tipo_cambio) {
    const rate = anyTC.Tipo_Cambio || anyTC.tipo_cambio;
    console.log(`   ✅ TC FALLBACK encontrado: ${rate}`);
    return rate;
  }
  
  // Log para ver qué fechas hay en tiposCambio
  const availableDates = [...new Set(tiposCambio.map(t => t.Fecha_Consumo || t.fecha_consumo))];
  console.log(`   ❌ NO encontrado. Fechas disponibles en tiposCambio: [${availableDates.join(', ')}]`);
  
  // Default
  return null;
};

const normalizeText = (value) => String(value || '').toLowerCase();

const truncateText = (value, maxLength = 50) => {
  if (!value) return '-';
  const str = String(value);
  if (str.length <= maxLength) return str;
  return `${str.slice(0, maxLength)}...`;
};

const getClassificationBadge = (clasificacion, isDarkMode) => {
  const normalizedClass = normalizeText(clasificacion);

  const badgeConfigs = {
    'facturacion': isDarkMode ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-400/45' : 'bg-emerald-100 text-emerald-700 border border-emerald-300',
    'no facturacion': isDarkMode ? 'bg-slate-500/20 text-slate-300 border border-slate-400/45' : 'bg-slate-100 text-slate-700 border border-slate-300',
    'contractual': isDarkMode ? 'bg-sky-500/20 text-sky-300 border border-sky-400/45' : 'bg-sky-100 text-sky-700 border border-sky-300',
  };

  for (const [key, classes] of Object.entries(badgeConfigs)) {
    if (normalizedClass.includes(key)) return classes;
  }

  return isDarkMode
    ? 'bg-gray-500/20 text-gray-300 border border-gray-400/45'
    : 'bg-gray-100 text-gray-700 border border-gray-300';
};

export default function DetalleFacturacionTable({
  records = [],
  isDarkMode = true,
  tiposCambio = [],
  monedaVisualizacion = 'USD'
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(1);

  // Debugging
  useEffect(() => {
    console.log('🔍 DetalleFacturacionTable props:', {
      recordsCount: records.length,
      tiposCambioCount: tiposCambio.length,
      monedaVisualizacion
    });
    if (tiposCambio.length > 0) {
      console.log('   Primeros tipos de cambio:', tiposCambio.slice(0, 3));
      const availableFechas = [...new Set(tiposCambio.map(t => t.Fecha_Consumo || t.fecha_consumo))];
      console.log('   📅 Fechas en tiposCambio:', availableFechas);
    }
    if (records.length > 0) {
      console.log('   Estructura del primer record:', Object.keys(records[0]));
      const recordFechas = [...new Set(records.map(r => r.Mes || r.Fecha_Consumo || r.fecha_consumo))];
      console.log('   📅 Fechas en records:', recordFechas.slice(0, 10));
    }
  }, [records, tiposCambio, monedaVisualizacion]);

  const normalizedRecords = useMemo(() => {
    console.log(`🔄 RECALCULANDO tabla: monedaVisualizacion=${monedaVisualizacion}, records=${records.length}, tiposCambio=${tiposCambio.length}`);
    const result = records.map((record, idx) => {
      // Aplicar conversión de moneda si es necesario
      // El backend retorna con mayúsculas: Precio_Neto, Costo_Real, Utilidad
      const recordMoneda = record.Moneda || record.Moneda_Consumo || record.moneda || record.moneda_consumo;
      console.log(`   Record ${idx}: ${record.Proveedor || record.proveedor} - Moneda: ${recordMoneda}, Precio: ${record.Precio_Neto || record.precio_neto}`);
      
      const precioNeto = convertPrice(record.Precio_Neto || record.precio_neto, record, monedaVisualizacion, tiposCambio);
      const costoReal = convertPrice(record.Costo_Real || record.costo_real, record, monedaVisualizacion, tiposCambio);
      const utilidad = convertPrice(record.Utilidad || record.utilidad, record, monedaVisualizacion, tiposCambio);
      
      return {
        ...record,
        fecha_formateada: formatDate(record.Mes || record.Fecha_Consumo || record.fecha_consumo),
        precio_neto_formateado: formatPrice(precioNeto),
        costo_real_formateado: formatPrice(costoReal),
        utilidad_formateada: formatPrice(utilidad),
        margen_formateado: (record.Margen || record.margen) ? `${parseFloat(record.Margen || record.margen).toFixed(2)}%` : '-',
        // Key ESTABLE: no incluir idx, usar identificadores únicos del record
        uniqueKey: `${record.Id_Cliente_Sap}-${record.Proveedor}-${record.Mes}-${record.Servicio}`.replace(/undefined/g, 'null')
      };
    });
    console.log(`✅ Tabla actualizada: ${result.length} registros. Primeros precios: [${result[0]?.precio_neto_formateado}, ${result[1]?.precio_neto_formateado}]`);
    return result;
  }, [records, monedaVisualizacion, tiposCambio]);

  const filteredRecords = useMemo(() => {
    const term = normalizeText(searchTerm).trim();
    if (!term) return normalizedRecords;

    return normalizedRecords.filter((record) => {
      return (
        normalizeText(record.Nombre_Cliente).includes(term) ||
        normalizeText(record.Proveedor).includes(term) ||
        normalizeText(record.Servicio).includes(term) ||
        normalizeText(record.Clasificacion).includes(term) ||
        normalizeText(record.Moneda || record.Moneda_Consumo).includes(term) ||
        normalizeText(record.Id_Cliente_Sap).includes(term)
      );
    });
  }, [normalizedRecords, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredRecords.length / rowsPerPage));

  const paginatedRecords = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return filteredRecords.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredRecords, currentPage, rowsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [rowsPerPage, searchTerm, records]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleExportExcel = () => {
    const rowsToExport = paginatedRecords.map((record) => ({
      'Fecha Consumo': record.fecha_formateada,
      'Cliente': record.Nombre_Cliente || '-',
      'Proveedor': record.Proveedor || '-',
      'Servicio': record.Servicio || '-',
      'Clasificación': record.Clasificacion || '-',
      'Precio Neto': record.precio_neto_formateado,
      'Costo Real': record.costo_real_formateado,
      'Utilidad': record.utilidad_formateada,
      'Margen': record.margen_formateado,
      'Moneda Original': record.Moneda_Consumo || '-',
      'Moneda Visualización': monedaVisualizacion,
      'Tipo Facturación': record.Tipo_Facturacion || '-'
    }));

    const worksheet = XLSX.utils.json_to_sheet(rowsToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Facturación');

    const now = new Date();
    const fileDate = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
    XLSX.writeFile(workbook, `detalle_facturacion_${fileDate}_${monedaVisualizacion}.xlsx`);
  };

  const containerClasses = isDarkMode
    ? 'bg-[#00261A] border border-[#00FF66]/25 rounded-xl p-6 shadow-lg'
    : 'bg-white border border-emerald-200 rounded-xl p-6 shadow-lg';

  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-white/75' : 'text-gray-600';
  const tableHeader = isDarkMode ? 'bg-[#00FF66]/8 text-[#bde8cc]' : 'bg-emerald-50 text-gray-700';
  const tableBorder = isDarkMode ? 'border-[#00FF66]/15' : 'border-gray-200';
  const rowHover = isDarkMode ? 'hover:bg-[#00FF66]/6' : 'hover:bg-emerald-50';

  const startRecord = filteredRecords.length === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endRecord = Math.min(currentPage * rowsPerPage, filteredRecords.length);

  return (
    <section className={containerClasses}>
      <div className="mb-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className={isDarkMode ? 'text-[#00FF66] text-xl font-bold' : 'text-emerald-700 text-xl font-bold'}>
              Detalle de Facturación
            </h3>
            <p className={`${textSecondary} text-sm mt-1`}>
              Registros detallados de consumo y facturación según filtros seleccionados.
            </p>
          </div>
          <div className={`px-3 py-1 rounded-lg border text-xs font-semibold ${isDarkMode ? 'bg-[#00FF66]/10 border-[#00FF66]/30 text-[#00FF66]' : 'bg-emerald-50 border-emerald-300 text-emerald-700'}`}>
            Visualizando en: <span className="font-bold">{monedaVisualizacion}</span>
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between mb-4">
        <div className="relative w-full lg:max-w-md">
          <Search className={`w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-[#1EEB8A]' : 'text-emerald-500'}`} />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar cliente, proveedor, servicio..."
            className={`w-full pl-9 pr-3 py-2 rounded-lg border text-sm ${
              isDarkMode
                ? 'bg-[#001f16] border-[#00FF66]/25 text-[#e0ffe8] placeholder:text-[#9ac9aa] focus:border-[#00FF66] focus:shadow-[0_0_8px_rgba(0,255,102,0.2)]'
                : 'bg-white border-gray-300 text-gray-900 placeholder:text-gray-500 focus:border-emerald-500'
            } focus:outline-none [transition:all_.25s_ease]`}
          />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className={`${textSecondary} text-sm`}>Registros por página</span>
            <select
              value={rowsPerPage}
              onChange={(e) => setRowsPerPage(Number(e.target.value))}
              className={`px-2 py-2 rounded-lg border text-sm ${
                isDarkMode
                  ? 'bg-[#001f16] border-[#00FF66]/25 text-[#e0ffe8] focus:border-[#00FF66]'
                  : 'bg-white border-gray-300 text-gray-900 focus:border-emerald-500'
              } focus:outline-none`}
            >
              {PAGE_SIZE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            onClick={handleExportExcel}
            disabled={paginatedRecords.length === 0}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-semibold [transition:all_.25s_ease] ${
              isDarkMode
                ? 'bg-[#00FF66]/10 border-[#00FF66]/35 text-[#00FF66] hover:bg-[#00FF66]/18 disabled:opacity-50 disabled:cursor-not-allowed'
                : 'bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed'
            }`}
          >
            <Download className="w-4 h-4" />
            Exportar Excel
          </button>
        </div>
      </div>

      {filteredRecords.length === 0 ? (
        <div className={`rounded-lg border ${tableBorder} px-4 py-10 text-center ${textSecondary}`}>
          No existen registros para los filtros seleccionados
        </div>
      ) : (
        <>
          <div className="overflow-x-auto [scrollbar-gutter:stable]">
            <table className="min-w-[1200px] w-full text-sm">
              <thead className={`${tableHeader} sticky top-0 z-10`}>
                <tr>
                  <th className={`px-3 py-3 text-left font-semibold border-b ${tableBorder}`}>Fecha</th>
                  <th className={`px-3 py-3 text-left font-semibold border-b ${tableBorder}`}>Cliente</th>
                  <th className={`px-3 py-3 text-left font-semibold border-b ${tableBorder}`}>Proveedor</th>
                  <th className={`px-3 py-3 text-left font-semibold border-b ${tableBorder}`}>Servicio</th>
                  <th className={`px-3 py-3 text-left font-semibold border-b ${tableBorder}`}>Clasificación</th>
                  <th className={`px-3 py-3 text-right font-semibold border-b ${tableBorder}`}>Precio Neto</th>
                  <th className={`px-3 py-3 text-right font-semibold border-b ${tableBorder}`}>Costo Real</th>
                  <th className={`px-3 py-3 text-right font-semibold border-b ${tableBorder}`}>Utilidad</th>
                  <th className={`px-3 py-3 text-right font-semibold border-b ${tableBorder}`}>Margen</th>
                  <th className={`px-3 py-3 text-left font-semibold border-b ${tableBorder}`}>Moneda</th>
                </tr>
              </thead>
              <tbody>
                {paginatedRecords.map((record) => (
                  <tr
                    key={record.uniqueKey}
                    className={`${rowHover} border-b ${tableBorder} [transition:background_.2s_ease]`}
                  >
                    <td className={`px-3 py-3 ${textPrimary}`}>{record.fecha_formateada}</td>
                    <td className={`px-3 py-3 ${textPrimary}`} title={record.Nombre_Cliente}>
                      {truncateText(record.Nombre_Cliente, 30)}
                    </td>
                    <td className={`px-3 py-3 ${textPrimary}`}>{record.Proveedor || '-'}</td>
                    <td className={`px-3 py-3 ${textPrimary}`} title={record.Servicio}>
                      {truncateText(record.Servicio, 25)}
                    </td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${getClassificationBadge(record.Clasificacion, isDarkMode)}`}>
                        {truncateText(record.Clasificacion, 12)}
                      </span>
                    </td>
                    <td className={`px-3 py-3 text-right ${textPrimary} font-semibold`}>
                      {record.precio_neto_formateado}
                    </td>
                    <td className={`px-3 py-3 text-right ${textPrimary} font-semibold`}>
                      {record.costo_real_formateado}
                    </td>
                    <td className={`px-3 py-3 text-right ${textPrimary} font-semibold`}>
                      {record.utilidad_formateada}
                    </td>
                    <td className={`px-3 py-3 text-right ${textPrimary} font-semibold`}>
                      {record.margen_formateado}
                    </td>
                    <td className={`px-3 py-3 ${textPrimary}`}>{record.Moneda || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between mt-4">
            <p className={`${textSecondary} text-sm`}>
              Mostrando {startRecord} - {endRecord} de {filteredRecords.length} registros
            </p>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className={`px-3 py-1.5 rounded-lg border text-sm ${
                  isDarkMode
                    ? 'border-[#00FF66]/25 text-[#bde8cc] hover:border-[#00FF66]/45 disabled:opacity-40'
                    : 'border-gray-300 text-gray-700 hover:border-emerald-400 disabled:opacity-40'
                }`}
              >
                Anterior
              </button>
              <span className={`${textPrimary} text-sm font-semibold`}>
                {currentPage} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className={`px-3 py-1.5 rounded-lg border text-sm ${
                  isDarkMode
                    ? 'border-[#00FF66]/25 text-[#bde8cc] hover:border-[#00FF66]/45 disabled:opacity-40'
                    : 'border-gray-300 text-gray-700 hover:border-emerald-400 disabled:opacity-40'
                }`}
              >
                Siguiente
              </button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
